import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import html2canvas from 'html2canvas';
import { 
  FaAward, FaDownload, FaPrint, FaWhatsapp, FaMagic, 
  FaCalendarAlt, FaFeatherAlt, FaArrowsAlt, FaUndo, FaUpload
} from 'react-icons/fa';

interface Member {
  id: number;
  name: string;
  email?: string;
  number?: string;
  company_name?: string;
  designation?: string;
}

/**
 * Utility to convert text to proper Title Case
 */
export const toTitleCase = (str: string): string => {
  if (!str) return '';
  const minorWords = new Set(['a', 'an', 'and', 'as', 'at', 'but', 'by', 'for', 'in', 'nor', 'of', 'on', 'or', 'so', 'the', 'to', 'up', 'yet', 'with']);
  
  return str
    .trim()
    .split(/\s+/)
    .map((word, index, arr) => {
      const upper = word.toUpperCase();
      if (['R2R', 'RMB', 'I', 'II', 'III', 'IV', 'DR.', 'DR', 'RTN', 'RTN.'].includes(upper)) {
        if (upper === 'DR') return 'Dr.';
        if (upper === 'RTN') return 'Rtn.';
        return upper;
      }
      const lower = word.toLowerCase();
      if (index > 0 && index < arr.length - 1 && minorWords.has(lower)) {
        return lower;
      }
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(' ');
};

// Draggable Canvas Element Wrapper Component
interface DraggableElementProps {
  enabled: boolean;
  position: { x: number; y: number };
  onMove: (pos: { x: number; y: number }) => void;
  label?: string;
  children: React.ReactNode;
  className?: string;
}

const DraggableElement: React.FC<DraggableElementProps> = ({ enabled, position, onMove, label, children, className = '' }) => {
  const [isDragging, setIsDragging] = useState(false);
  const startPosRef = useRef({ x: 0, y: 0 });
  const elementPosRef = useRef({ x: 0, y: 0 });

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!enabled) return;
    e.preventDefault();
    setIsDragging(true);
    startPosRef.current = { x: e.clientX, y: e.clientY };
    elementPosRef.current = { ...position };
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      const dx = e.clientX - startPosRef.current.x;
      const dy = e.clientY - startPosRef.current.y;
      onMove({
        x: elementPosRef.current.x + dx,
        y: elementPosRef.current.y + dy
      });
    };

    const handleMouseUp = () => {
      if (isDragging) setIsDragging(false);
    };

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, onMove]);

  return (
    <div
      onMouseDown={handleMouseDown}
      style={{
        transform: `translate(${position.x}px, ${position.y}px)`,
        cursor: enabled ? (isDragging ? 'grabbing' : 'grab') : 'default'
      }}
      className={`transition-shadow ${enabled ? 'hover:outline-2 hover:outline-dashed hover:outline-amber-500 relative group z-40' : ''} ${className}`}
    >
      {enabled && label && (
        <span className="absolute -top-5 left-1/2 -translate-x-1/2 bg-amber-400 text-slate-950 font-black text-[9px] px-1.5 py-0.5 rounded shadow pointer-events-none opacity-80 group-hover:opacity-100 whitespace-nowrap z-50 flex items-center gap-1">
          <FaArrowsAlt size={8} /> Drag {label}
        </span>
      )}
      {children}
    </div>
  );
};

const CertificateGenerator: React.FC = () => {
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedMemberId, setSelectedMemberId] = useState<string>('');
  const certificateRef = useRef<HTMLDivElement>(null);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [isDragModeEnabled, setIsDragModeEnabled] = useState<boolean>(false);

  // Element positions state for Drag & Drop on Canvas
  const [positions, setPositions] = useState({
    headerLogo: { x: 0, y: 0 },
    titleHeader: { x: 0, y: 0 },
    recipientName: { x: 0, y: 0 },
    awardTitle: { x: 0, y: 0 },
    citation: { x: 0, y: 0 },
    signatures: { x: 0, y: 0 },
    seal: { x: 0, y: 0 },
    footer: { x: 0, y: 0 }
  });

  const resetPositions = () => {
    setPositions({
      headerLogo: { x: 0, y: 0 },
      titleHeader: { x: 0, y: 0 },
      recipientName: { x: 0, y: 0 },
      awardTitle: { x: 0, y: 0 },
      citation: { x: 0, y: 0 },
      signatures: { x: 0, y: 0 },
      seal: { x: 0, y: 0 },
      footer: { x: 0, y: 0 }
    });
  };

  // Form State initialized to exact text in the prompt image
  const [form, setForm] = useState({
    recipientName: 'Recipient Name',
    awardTitle: 'R2R CHAMPION',
    subtitle: 'MAXIMUM MEMBER CONNECTIONS & MEANINGFUL RELATIONSHIPS',
    citationLine1: 'In recognition of your Maximum Connecting with the Members and building strong relationships and creating meaningful connections within the fellowship.',
    citationLine2: 'Thank you for strengthening the RMB network and inspiring others by your example.',
    chairmanName: 'Siddharth Makharia',
    chairmanTitle: 'CHAIRMAN',
    viceChairName: 'Gaurav Mathur',
    viceChairTitle: 'VICE-CHAIR',
    secretaryName: 'Ashish Bhardwaj',
    secretaryTitle: 'SECRETARY',
    treasurerName: 'Dr. Megha Sharma',
    treasurerTitle: 'TREASURER',
    certificateDate: new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
  });

  // Admin Certificate Template & Signature Uploads State
  const [certSettings, setCertSettings] = useState<any>({
    template_bg: '',
    sig_president: '',
    sig_secretary: '',
    sig_treasurer: '',
    session_year: '2024-2025',
    president_name: 'Siddharth Makharia',
    president_title: 'CHAIRMAN',
    secretary_name: 'Ashish Bhardwaj',
    secretary_title: 'SECRETARY',
    treasurer_name: 'Dr. Megha Sharma',
    treasurer_title: 'TREASURER'
  });

  const [bgFile, setBgFile] = useState<File | null>(null);
  const [sigPresFile, setSigPresFile] = useState<File | null>(null);
  const [sigSecFile, setSigSecFile] = useState<File | null>(null);
  const [sigTreasFile, setSigTreasFile] = useState<File | null>(null);
  const [savingSettings, setSavingSettings] = useState<boolean>(false);

  const fetchCertSettings = async () => {
    try {
      const res = await axios.get('https://rmbbackend.rmbjaipur.com/auth/certificate-settings');
      if (res.data?.success && res.data.settings) {
        const s = res.data.settings;
        setCertSettings(s);
        setForm(prev => ({
          ...prev,
          chairmanName: s.president_name || prev.chairmanName,
          chairmanTitle: s.president_title || prev.chairmanTitle,
          secretaryName: s.secretary_name || prev.secretaryName,
          secretaryTitle: s.secretary_title || prev.secretaryTitle,
          treasurerName: s.treasurer_name || prev.treasurerName,
          treasurerTitle: s.treasurer_title || prev.treasurerTitle,
        }));
      }
    } catch (err) {
      console.error('Error fetching certificate settings:', err);
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      const formData = new FormData();
      formData.append('session_year', certSettings.session_year);
      formData.append('president_name', form.chairmanName);
      formData.append('president_title', form.chairmanTitle);
      formData.append('secretary_name', form.secretaryName);
      formData.append('secretary_title', form.secretaryTitle);
      formData.append('treasurer_name', form.treasurerName);
      formData.append('treasurer_title', form.treasurerTitle);

      if (bgFile) formData.append('template_bg', bgFile);
      if (sigPresFile) formData.append('sig_president', sigPresFile);
      if (sigSecFile) formData.append('sig_secretary', sigSecFile);
      if (sigTreasFile) formData.append('sig_treasurer', sigTreasFile);

      const res = await axios.post('https://rmbbackend.rmbjaipur.com/auth/certificate-settings', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (res.data?.success) {
        alert('🎉 Certificate template design, leadership signatures, and session settings saved successfully!');
        setCertSettings(res.data.settings);
        setBgFile(null);
        setSigPresFile(null);
        setSigSecFile(null);
        setSigTreasFile(null);
      }
    } catch (err: any) {
      console.error('Error saving certificate settings:', err);
      alert(err.response?.data?.error || 'Failed to save certificate settings.');
    } finally {
      setSavingSettings(false);
    }
  };

  useEffect(() => {
    fetchMembers();
    fetchCertSettings();
  }, []);

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const res = await axios.get('https://rmbbackend.rmbjaipur.com/auth/allusersinfo');
      const list = Array.isArray(res.data) ? res.data : (res.data.data || []);
      const activeMembers = list.filter((u: any) => u.delete !== 1 && u.role !== 'super-admin');
      activeMembers.sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''));
      setMembers(activeMembers);

      if (activeMembers.length > 0) {
        setSelectedMemberId(String(activeMembers[0].id));
        setForm(prev => ({
          ...prev,
          recipientName: toTitleCase(activeMembers[0].name || 'Recipient Name')
        }));
      }
    } catch (err) {
      console.error('Error loading members for Certificate:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMemberSelect = (idStr: string) => {
    setSelectedMemberId(idStr);
    const m = members.find(u => String(u.id) === idStr);
    if (m) {
      setForm(prev => ({
        ...prev,
        recipientName: toTitleCase(m.name)
      }));
    }
  };

  // Automate camel casing and Title casing fixes across all fields
  const handleFixCasing = () => {
    setForm(prev => ({
      ...prev,
      recipientName: toTitleCase(prev.recipientName),
      awardTitle: prev.awardTitle.toUpperCase(),
      subtitle: prev.subtitle.toUpperCase(),
      citationLine1: toTitleCase(prev.citationLine1.toLowerCase()).replace(/\bWith The\b/gi, 'with the').replace(/\bAnd\b/gi, 'and').replace(/\bOf\b/gi, 'of').replace(/\bIn\b/gi, 'In'),
      citationLine2: toTitleCase(prev.citationLine2.toLowerCase()).replace(/\bBy Your\b/gi, 'by your').replace(/\bAnd\b/gi, 'and'),
      chairmanName: toTitleCase(prev.chairmanName),
      viceChairName: toTitleCase(prev.viceChairName),
      secretaryName: toTitleCase(prev.secretaryName),
      treasurerName: toTitleCase(prev.treasurerName)
    }));
  };

  // Drag & Drop File Upload Handlers for Dropzones
  const handleDropFile = (e: React.DragEvent, type: 'bg' | 'pres' | 'sec' | 'treas') => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    if (type === 'bg') setBgFile(file);
    if (type === 'pres') setSigPresFile(file);
    if (type === 'sec') setSigSecFile(file);
    if (type === 'treas') setSigTreasFile(file);
  };

  // Export high resolution PNG image of certificate
  const handleDownloadPng = async () => {
    if (!certificateRef.current) return;
    setIsExporting(true);
    try {
      const canvas = await html2canvas(certificateRef.current, {
        scale: 3, // High-DPI crisp export
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#FAF8F3'
      });

      const image = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = image;
      const safeName = form.recipientName.replace(/[^a-zA-Z0-9]/g, '_');
      link.download = `Certificate_${safeName}_R2R_Champion.png`;
      link.click();
    } catch (err) {
      console.error('Failed to generate certificate PNG:', err);
      alert('Error generating certificate image. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  // Browser print trigger
  const handlePrint = () => {
    window.print();
  };

  // WhatsApp sharing
  const handleShareWhatsApp = () => {
    const msg = `🏆 *R2R CHAMPION CERTIFICATE OF APPRECIATION* 📜\n\n` +
      `We proudly present this certificate to *${form.recipientName}* for being an *${form.awardTitle}*!\n\n` +
      `🎗️ *${form.subtitle}*\n` +
      `💬 ${form.citationLine1}\n\n` +
      `_Rotary Means Business Fellowship Jaipur_`;
    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(msg)}`, '_blank');
  };

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-6 text-slate-100">
      
      {/* HEADER BAR */}
      <div className="bg-[#10192D] p-5 rounded-2xl border border-[#2A3B5C] shadow-2xl flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-black text-amber-400 flex items-center gap-2">
            <FaAward className="text-amber-400" /> Certificate Studio & Generator
          </h2>
          <p className="text-xs text-slate-400 mt-1">
            Generate, customize, fix camel casing/title casing, and download high-resolution RMB Jaipur Certificates of Appreciation.
          </p>
        </div>

        {/* Action Controls */}
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={handleFixCasing}
            className="px-4 py-2 bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-400 hover:to-yellow-500 text-[#0A2451] font-black text-xs rounded-xl shadow-lg flex items-center gap-2 transition cursor-pointer border border-amber-300 active:scale-95"
            title="Automatically format names and titles into proper Title Case / Camel Case"
          >
            <FaMagic size={14} /> ✨ Fix Camel Casing & Formatting
          </button>
          
          <button
            onClick={handleDownloadPng}
            disabled={isExporting}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-black text-xs rounded-xl shadow-lg flex items-center gap-2 transition cursor-pointer active:scale-95"
          >
            <FaDownload size={14} /> {isExporting ? 'Generating PNG...' : 'Download HD PNG'}
          </button>

          <button
            onClick={handlePrint}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white font-black text-xs rounded-xl shadow-lg flex items-center gap-2 transition cursor-pointer active:scale-95"
          >
            <FaPrint size={14} /> Print Certificate
          </button>

          <button
            onClick={handleShareWhatsApp}
            className="px-4 py-2 bg-emerald-700 hover:bg-emerald-600 text-white font-black text-xs rounded-xl shadow-lg flex items-center gap-2 transition cursor-pointer active:scale-95"
          >
            <FaWhatsapp size={14} /> Share WhatsApp
          </button>
        </div>
      </div>

      {/* FORM CONTROLS PANEL */}
      <div className="bg-[#131E32] p-5 rounded-2xl border border-[#2A3B5C] shadow-xl space-y-4">
        <h3 className="text-sm font-bold text-amber-300 flex items-center gap-2 border-b border-[#2A3B5C] pb-2">
          <FaFeatherAlt /> Customize Certificate Details & Recipient
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
          {/* Select Member from Database */}
          <div>
            <label className="font-bold text-slate-300 block mb-1">Select RMB Member Roster</label>
            <select
              value={selectedMemberId}
              onChange={(e) => handleMemberSelect(e.target.value)}
              className="w-full p-2.5 bg-[#1E2B45] border border-[#2A3B5C] rounded-xl text-white outline-none focus:border-amber-400 font-medium"
            >
              <option value="">-- Choose Member --</option>
              {members.map(m => (
                <option key={m.id} value={m.id}>
                  {m.name} {m.company_name ? `(${m.company_name})` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Recipient Name */}
          <div>
            <label className="font-bold text-slate-300 block mb-1">Recipient Name *</label>
            <input
              type="text"
              value={form.recipientName}
              onChange={(e) => setForm({ ...form, recipientName: e.target.value })}
              className="w-full p-2.5 bg-[#1E2B45] border border-[#2A3B5C] rounded-xl text-amber-300 font-bold outline-none focus:border-amber-400"
              placeholder="e.g. Recipient Name"
            />
          </div>

          {/* Award Title */}
          <div>
            <label className="font-bold text-slate-300 block mb-1">Award Title Header *</label>
            <input
              type="text"
              value={form.awardTitle}
              onChange={(e) => setForm({ ...form, awardTitle: e.target.value })}
              className="w-full p-2.5 bg-[#1E2B45] border border-[#2A3B5C] rounded-xl text-white font-bold outline-none focus:border-amber-400"
              placeholder="e.g. R2R CHAMPION"
            />
          </div>

          {/* Subtitle */}
          <div className="md:col-span-2">
            <label className="font-bold text-slate-300 block mb-1">Award Category / Subtitle</label>
            <input
              type="text"
              value={form.subtitle}
              onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
              className="w-full p-2.5 bg-[#1E2B45] border border-[#2A3B5C] rounded-xl text-white font-bold outline-none focus:border-amber-400"
            />
          </div>

          {/* Date */}
          <div>
            <label className="font-bold text-slate-300 block mb-1">Issue Date</label>
            <input
              type="text"
              value={form.certificateDate}
              onChange={(e) => setForm({ ...form, certificateDate: e.target.value })}
              className="w-full p-2.5 bg-[#1E2B45] border border-[#2A3B5C] rounded-xl text-white font-semibold outline-none focus:border-amber-400"
            />
          </div>

          {/* Citation Paragraph 1 */}
          <div className="md:col-span-3">
            <label className="font-bold text-slate-300 block mb-1">Citation Main Paragraph</label>
            <textarea
              rows={2}
              value={form.citationLine1}
              onChange={(e) => setForm({ ...form, citationLine1: e.target.value })}
              className="w-full p-2.5 bg-[#1E2B45] border border-[#2A3B5C] rounded-xl text-slate-200 outline-none focus:border-amber-400"
            />
          </div>

          {/* Signatories (4 Columns) */}
          <div>
            <label className="font-bold text-slate-300 block mb-1">Chairman Name</label>
            <input
              type="text"
              value={form.chairmanName}
              onChange={(e) => setForm({ ...form, chairmanName: e.target.value })}
              className="w-full p-2 bg-[#1E2B45] border border-[#2A3B5C] rounded-lg text-white font-medium outline-none"
            />
          </div>

          <div>
            <label className="font-bold text-slate-300 block mb-1">Vice-Chair Name</label>
            <input
              type="text"
              value={form.viceChairName}
              onChange={(e) => setForm({ ...form, viceChairName: e.target.value })}
              className="w-full p-2 bg-[#1E2B45] border border-[#2A3B5C] rounded-lg text-white font-medium outline-none"
            />
          </div>

          <div>
            <label className="font-bold text-slate-300 block mb-1">Secretary Name</label>
            <input
              type="text"
              value={form.secretaryName}
              onChange={(e) => setForm({ ...form, secretaryName: e.target.value })}
              className="w-full p-2 bg-[#1E2B45] border border-[#2A3B5C] rounded-lg text-white font-medium outline-none"
            />
          </div>

          <div>
            <label className="font-bold text-slate-300 block mb-1">Treasurer Name</label>
            <input
              type="text"
              value={form.treasurerName}
              onChange={(e) => setForm({ ...form, treasurerName: e.target.value })}
              className="w-full p-2 bg-[#1E2B45] border border-[#2A3B5C] rounded-lg text-white font-medium outline-none"
            />
          </div>
        </div>

        {/* ADMIN TEMPLATE DESIGN & LEADERSHIP DRAG & DROP SIGNATURE UPLOADS */}
        <div className="bg-[#10192D] p-4 rounded-xl border border-amber-400/40 space-y-3">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 border-b border-[#2A3B5C] pb-2">
            <div>
              <h4 className="text-xs font-black text-amber-400 uppercase tracking-wider flex items-center gap-1.5">
                <span>🎨</span> Custom Certificate Design &amp; Drag-and-Drop Image Dropzones
              </h4>
              <p className="text-[11px] text-slate-400">
                Drag and drop custom background templates and leadership PNG signatures directly into the dropzone boxes below.
              </p>
            </div>

            <button
              type="button"
              onClick={handleSaveSettings}
              disabled={savingSettings}
              className="px-4 py-2 bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-500 hover:from-amber-300 hover:to-yellow-300 text-slate-950 font-black text-xs rounded-xl shadow-lg transition cursor-pointer disabled:opacity-50 flex items-center gap-1.5"
            >
              <span>💾</span> <span>{savingSettings ? 'Saving Settings...' : 'Save Template & Signatures'}</span>
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 text-xs">
            {/* Session / Year */}
            <div>
              <label className="font-bold text-slate-300 block mb-1">Rotary Session / Year</label>
              <input
                type="text"
                value={certSettings.session_year || '2024-2025'}
                onChange={(e) => setCertSettings({ ...certSettings, session_year: e.target.value })}
                className="w-full p-2 bg-[#1E2B45] border border-[#2A3B5C] rounded-lg text-amber-300 font-bold outline-none"
                placeholder="e.g. 2024-2025"
              />
            </div>

            {/* Background Design Drag & Drop Zone */}
            <div>
              <label className="font-bold text-slate-300 block mb-1">Certificate Background</label>
              <div 
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => handleDropFile(e, 'bg')}
                className="border-2 border-dashed border-slate-600 hover:border-amber-400 p-2 rounded-xl text-center cursor-pointer transition bg-[#1E2B45]"
              >
                <div className="text-[10px] font-bold text-amber-300 flex items-center justify-center gap-1">
                  <FaUpload size={10} /> Drag &amp; Drop Background
                </div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setBgFile(e.target.files?.[0] || null)}
                  className="w-full text-[10px] text-slate-400 file:mr-1 file:py-0.5 file:px-1.5 file:rounded file:border-0 file:text-[9px] file:font-black file:bg-amber-400 file:text-slate-950 cursor-pointer mt-1"
                />
              </div>
              {certSettings.template_bg && (
                <span className="text-[10px] text-emerald-400 font-bold block mt-1">✓ Template Uploaded</span>
              )}
            </div>

            {/* President Drag & Drop Zone */}
            <div>
              <label className="font-bold text-slate-300 block mb-1">Chairman Signature</label>
              <div 
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => handleDropFile(e, 'pres')}
                className="border-2 border-dashed border-slate-600 hover:border-amber-400 p-2 rounded-xl text-center cursor-pointer transition bg-[#1E2B45]"
              >
                <div className="text-[10px] font-bold text-amber-300 flex items-center justify-center gap-1">
                  <FaUpload size={10} /> Drag &amp; Drop Signature
                </div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setSigPresFile(e.target.files?.[0] || null)}
                  className="w-full text-[10px] text-slate-400 file:mr-1 file:py-0.5 file:px-1.5 file:rounded file:border-0 file:text-[9px] file:font-black file:bg-amber-400 file:text-slate-950 cursor-pointer mt-1"
                />
              </div>
              {certSettings.sig_president && (
                <span className="text-[10px] text-emerald-400 font-bold block mt-1">✓ Signature Saved</span>
              )}
            </div>

            {/* Secretary Drag & Drop Zone */}
            <div>
              <label className="font-bold text-slate-300 block mb-1">Secretary Signature</label>
              <div 
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => handleDropFile(e, 'sec')}
                className="border-2 border-dashed border-slate-600 hover:border-amber-400 p-2 rounded-xl text-center cursor-pointer transition bg-[#1E2B45]"
              >
                <div className="text-[10px] font-bold text-amber-300 flex items-center justify-center gap-1">
                  <FaUpload size={10} /> Drag &amp; Drop Signature
                </div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setSigSecFile(e.target.files?.[0] || null)}
                  className="w-full text-[10px] text-slate-400 file:mr-1 file:py-0.5 file:px-1.5 file:rounded file:border-0 file:text-[9px] file:font-black file:bg-amber-400 file:text-slate-950 cursor-pointer mt-1"
                />
              </div>
              {certSettings.sig_secretary && (
                <span className="text-[10px] text-emerald-400 font-bold block mt-1">✓ Signature Saved</span>
              )}
            </div>

            {/* Treasurer Drag & Drop Zone */}
            <div>
              <label className="font-bold text-slate-300 block mb-1">Treasurer Signature</label>
              <div 
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => handleDropFile(e, 'treas')}
                className="border-2 border-dashed border-slate-600 hover:border-amber-400 p-2 rounded-xl text-center cursor-pointer transition bg-[#1E2B45]"
              >
                <div className="text-[10px] font-bold text-amber-300 flex items-center justify-center gap-1">
                  <FaUpload size={10} /> Drag &amp; Drop Signature
                </div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setSigTreasFile(e.target.files?.[0] || null)}
                  className="w-full text-[10px] text-slate-400 file:mr-1 file:py-0.5 file:px-1.5 file:rounded file:border-0 file:text-[9px] file:font-black file:bg-amber-400 file:text-slate-950 cursor-pointer mt-1"
                />
              </div>
              {certSettings.sig_treasurer && (
                <span className="text-[10px] text-emerald-400 font-bold block mt-1">✓ Signature Saved</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* CANVAS DRAG & DROP REPOSITIONING TOOLBAR */}
      <div className="bg-[#10192D] p-3.5 rounded-xl border border-[#2A3B5C] flex flex-col sm:flex-row justify-between items-center gap-3">
        <div className="flex items-center gap-2">
          <FaArrowsAlt className="text-amber-400 text-lg" />
          <div>
            <h4 className="text-xs font-black text-white uppercase tracking-wider">Canvas Element Repositioning Studio</h4>
            <p className="text-[11px] text-slate-400">Click the toggle button to drag any certificate text, title, or signature block around the canvas.</p>
          </div>
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <button
            type="button"
            onClick={() => setIsDragModeEnabled(prev => !prev)}
            className={`px-4 py-2 rounded-xl font-black text-xs transition cursor-pointer flex items-center justify-center gap-1.5 shadow ${
              isDragModeEnabled
                ? 'bg-amber-400 text-slate-950 shadow-amber-400/30'
                : 'bg-[#1E2B45] text-slate-300 border border-[#2A3B5C] hover:text-white'
            }`}
          >
            <FaArrowsAlt />
            <span>{isDragModeEnabled ? '✓ Drag Mode Active (Click to Lock)' : '🖱️ Enable Drag & Drop Mode'}</span>
          </button>

          <button
            type="button"
            onClick={resetPositions}
            className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white font-bold text-xs rounded-xl border border-slate-700 transition cursor-pointer flex items-center justify-center gap-1"
            title="Reset layout back to default center alignment"
          >
            <FaUndo /> Reset Layout
          </button>
        </div>
      </div>

      {/* CERTIFICATE PREVIEW & PRINT CONTAINER */}
      <div className="overflow-x-auto pb-6 flex justify-center">
        <div 
          ref={certificateRef}
          id="printable-certificate-frame"
          className="relative w-[1000px] h-[670px] bg-[#FAF8F3] text-[#0A2451] p-6 shadow-2xl flex flex-col justify-between overflow-hidden select-none border border-slate-300"
          style={{ fontFamily: "'Nunito', serif" }}
        >
          {/* === TOP-LEFT CORNER NAVY & GOLD CURVES === */}
          <svg className="absolute top-0 left-0 w-[260px] h-[260px] pointer-events-none z-0" viewBox="0 0 300 300" fill="none">
            <path d="M 0,0 L 260,0 C 180,90 90,180 0,260 Z" fill="#E6C200" opacity="0.9" />
            <path d="M 0,0 L 250,0 C 170,85 85,170 0,250 Z" fill="#B8860B" />
            <path d="M 0,0 L 235,0 C 150,75 75,150 0,235 Z" fill="#0A2451" />
            <path d="M 0,0 L 190,0 C 120,60 60,120 0,190 Z" fill="#E6C200" opacity="0.85" />
            <path d="M 0,0 L 180,0 C 110,55 55,110 0,180 Z" fill="#0A2451" />
          </svg>

          {/* === BOTTOM-RIGHT CORNER NAVY & GOLD CURVES === */}
          <svg className="absolute bottom-0 right-0 w-[260px] h-[260px] pointer-events-none z-0" viewBox="0 0 300 300" fill="none">
            <path d="M 300,300 L 40,300 C 120,210 210,120 300,40 Z" fill="#E6C200" opacity="0.9" />
            <path d="M 300,300 L 50,300 C 130,215 215,130 300,50 Z" fill="#B8860B" />
            <path d="M 300,300 L 65,300 C 150,225 225,150 300,65 Z" fill="#0A2451" />
            <path d="M 300,300 L 110,300 C 180,240 240,180 300,110 Z" fill="#E6C200" opacity="0.85" />
            <path d="M 300,300 L 120,300 C 190,245 245,190 300,120 Z" fill="#0A2451" />
          </svg>

          {/* === INNER ELEGANT DOUBLE GOLD BORDER === */}
          <div className="absolute inset-5 border-2 border-[#C5A059] pointer-events-none z-10 rounded-sm">
            <div className="absolute inset-[3px] border border-[#C5A059] opacity-70"></div>
            
            {/* Top Left Filigree Scroll */}
            <svg className="absolute top-1 left-1 w-8 h-8 text-[#C5A059]" viewBox="0 0 100 100" fill="currentColor">
              <path d="M10,10 C40,10 50,20 50,50 C20,50 10,40 10,10 Z M20,20 C30,20 35,25 35,35 C25,35 20,30 20,20 Z" />
            </svg>
            {/* Top Right Filigree Scroll */}
            <svg className="absolute top-1 right-1 w-8 h-8 text-[#C5A059]" viewBox="0 0 100 100" fill="currentColor">
              <path d="M90,10 C60,10 50,20 50,50 C80,50 90,40 90,10 Z M80,20 C70,20 65,25 65,35 C75,35 80,30 80,20 Z" />
            </svg>
            {/* Bottom Left Filigree Scroll */}
            <svg className="absolute bottom-1 left-1 w-8 h-8 text-[#C5A059]" viewBox="0 0 100 100" fill="currentColor">
              <path d="M10,90 C40,90 50,80 50,50 C20,50 10,60 10,90 Z M20,80 C30,80 35,75 35,65 C25,65 20,70 20,80 Z" />
            </svg>
            {/* Bottom Right Filigree Scroll */}
            <svg className="absolute bottom-1 right-1 w-8 h-8 text-[#C5A059]" viewBox="0 0 100 100" fill="currentColor">
              <path d="M90,90 C60,90 50,80 50,50 C80,50 90,60 90,90 Z M80,80 C70,80 65,75 65,65 C75,65 80,70 80,80 Z" />
            </svg>
          </div>

          {/* === CERTIFICATE CONTENT (Z-20) === */}
          <div className="relative z-20 h-full flex flex-col justify-between px-10 py-6 text-center">
            
            {/* TOP HEADER LOGOS */}
            <DraggableElement
              enabled={isDragModeEnabled}
              position={positions.headerLogo}
              onMove={(pos) => setPositions(p => ({ ...p, headerLogo: pos }))}
              label="Header Logos"
            >
              <div className="flex items-center justify-between pt-2">
                {/* Left: Rotary Emblem */}
                <div className="flex items-center gap-2 pl-24">
                  <span className="text-[#0A2451] font-black text-2xl tracking-tighter" style={{ fontFamily: 'Georgia, serif' }}>
                    Rotary
                  </span>
                  <svg className="w-10 h-10 text-amber-500 flex-shrink-0" viewBox="0 0 100 100" fill="currentColor">
                    <circle cx="50" cy="50" r="45" fill="#E6C200" stroke="#0A2451" strokeWidth="4" />
                    <circle cx="50" cy="50" r="25" fill="#FAF8F3" stroke="#0A2451" strokeWidth="3" />
                    <circle cx="50" cy="50" r="10" fill="#0A2451" />
                    <path d="M50 5 L50 25 M50 75 L50 95 M5 50 L25 50 M75 50 L95 50 M18 18 L32 32 M68 68 L82 82 M18 82 L32 68 M68 32 L82 18" stroke="#0A2451" strokeWidth="5" />
                  </svg>
                </div>

                {/* Center: RMB Badge */}
                <div className="bg-[#0A2451] text-amber-400 px-4 py-1.5 rounded-lg border-2 border-amber-400 shadow-md flex items-center gap-2">
                  <span className="font-black text-xl tracking-widest">RMB</span>
                  <span className="text-xs">🤝</span>
                </div>

                {/* Right: Rotary Means Business Fellowship Jaipur Badge */}
                <div className="flex flex-col items-end pr-8">
                  <div className="text-[#0A2451] font-black text-[13px] tracking-wider uppercase border-b-2 border-amber-500 pb-0.5">
                    Rotary Means Business
                  </div>
                  <div className="text-amber-600 font-extrabold text-[12px] tracking-widest uppercase">
                    Fellowship
                  </div>
                  <div className="bg-[#0A2451] text-white text-[9px] font-black tracking-widest px-3 py-0.5 rounded-sm uppercase mt-0.5">
                    Jaipur
                  </div>
                </div>
              </div>
            </DraggableElement>

            {/* MAIN CERTIFICATE TITLES */}
            <DraggableElement
              enabled={isDragModeEnabled}
              position={positions.titleHeader}
              onMove={(pos) => setPositions(p => ({ ...p, titleHeader: pos }))}
              label="Certificate Header"
            >
              <div className="space-y-1 mt-3">
                <h1 className="text-4xl sm:text-5xl font-black text-[#0A2451] tracking-[0.25em] uppercase" style={{ fontFamily: "'Cinzel', 'Playfair Display', Georgia, serif" }}>
                  CERTIFICATE
                </h1>
                <div className="flex items-center justify-center gap-4 text-amber-600 font-bold text-sm tracking-[0.3em] uppercase">
                  <span className="h-[1px] w-16 bg-amber-500 inline-block"></span>
                  <span>OF APPRECIATION</span>
                  <span className="h-[1px] w-16 bg-amber-500 inline-block"></span>
                </div>
                <div className="text-xs font-semibold text-slate-600 tracking-[0.2em] uppercase pt-2 flex items-center justify-center gap-2">
                  <span>❖</span> THIS CERTIFICATE IS PROUDLY PRESENTED TO <span>❖</span>
                </div>
              </div>
            </DraggableElement>

            {/* RECIPIENT NAME (CURSIVE SCRIPT) */}
            <DraggableElement
              enabled={isDragModeEnabled}
              position={positions.recipientName}
              onMove={(pos) => setPositions(p => ({ ...p, recipientName: pos }))}
              label="Recipient Name"
            >
              <div className="my-2">
                <span 
                  className="text-4xl sm:text-5xl text-[#0A2451] font-normal tracking-wide leading-relaxed inline-block border-b-2 border-amber-400/60 px-8 pb-1"
                  style={{ fontFamily: "'Great Vibes', 'Alex Brush', cursive" }}
                >
                  {form.recipientName || 'Recipient Name'}
                </span>
              </div>
            </DraggableElement>

            {/* AWARD TITLE SECTION WITH GOLD LAURELS */}
            <DraggableElement
              enabled={isDragModeEnabled}
              position={positions.awardTitle}
              onMove={(pos) => setPositions(p => ({ ...p, awardTitle: pos }))}
              label="Award Title"
            >
              <div className="space-y-1">
                <div className="text-xs font-bold text-amber-600 tracking-[0.25em] uppercase flex items-center justify-center gap-2">
                  <span className="h-[1px] w-10 bg-amber-500 inline-block"></span>
                  <span>FOR BEING AN</span>
                  <span className="h-[1px] w-10 bg-amber-500 inline-block"></span>
                </div>

                <div className="flex items-center justify-center gap-3">
                  <svg className="w-8 h-10 text-amber-500 opacity-90" viewBox="0 0 100 100" fill="currentColor">
                    <path d="M50 90 C30 70 20 40 30 10 C35 25 45 40 50 90 Z M35 30 C20 25 15 15 25 15 Z M30 50 C15 45 10 35 20 35 Z M35 70 C20 65 15 55 25 55 Z" />
                  </svg>

                  <h2 className="text-3xl sm:text-4xl font-black text-[#0A2451] tracking-wider uppercase" style={{ fontFamily: "'Cinzel', Georgia, serif" }}>
                    {form.awardTitle}
                  </h2>

                  <svg className="w-8 h-10 text-amber-500 opacity-90 transform scale-x-[-1]" viewBox="0 0 100 100" fill="currentColor">
                    <path d="M50 90 C30 70 20 40 30 10 C35 25 45 40 50 90 Z M35 30 C20 25 15 15 25 15 Z M30 50 C15 45 10 35 20 35 Z M35 70 C20 65 15 55 25 55 Z" />
                  </svg>
                </div>

                <div className="text-[11px] font-extrabold text-amber-700 tracking-[0.18em] uppercase">
                  {form.subtitle}
                </div>
              </div>
            </DraggableElement>

            {/* CITATION PARAGRAPH */}
            <DraggableElement
              enabled={isDragModeEnabled}
              position={positions.citation}
              onMove={(pos) => setPositions(p => ({ ...p, citation: pos }))}
              label="Citation Text"
            >
              <div className="max-w-2xl mx-auto space-y-1.5 my-2">
                <p className="text-xs text-slate-700 leading-relaxed font-medium">
                  In recognition of your <strong className="text-[#0A2451] font-extrabold">{form.citationLine1.replace(/^In recognition of your /i, '')}</strong>
                </p>
                <p className="text-[11px] text-slate-600 font-medium">
                  {form.citationLine2}
                </p>
              </div>
            </DraggableElement>

            {/* CUSTOM UPLOADED BACKGROUND DESIGN TEMPLATE */}
            {certSettings.template_bg && (
              <img
                src={`https://rmbbackend.rmbjaipur.com/${certSettings.template_bg}`}
                alt="Certificate Design Background"
                className="absolute inset-0 w-full h-full object-cover z-0 pointer-events-none"
              />
            )}

            {/* LEADERSHIP SIGNATURES (4 COLUMNS AT BOTTOM) */}
            <DraggableElement
              enabled={isDragModeEnabled}
              position={positions.signatures}
              onMove={(pos) => setPositions(p => ({ ...p, signatures: pos }))}
              label="Signatures Block"
            >
              <div className="grid grid-cols-4 gap-2 pt-4 border-t border-amber-300/40 max-w-3xl mx-auto w-full">
                {/* Chairman */}
                <div className="flex flex-col items-center text-center justify-end h-20">
                  {certSettings.sig_president ? (
                    <img
                      src={`https://rmbbackend.rmbjaipur.com/${certSettings.sig_president}`}
                      alt="Chairman Signature"
                      className="h-9 w-auto object-contain mb-0.5"
                    />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-[#0A2451] text-amber-400 flex items-center justify-center text-xs mb-1">
                      👤
                    </div>
                  )}
                  <div className="text-[10px] font-black text-[#0A2451] tracking-wider uppercase">
                    {form.chairmanTitle}
                  </div>
                  <div className="h-[1px] w-12 bg-amber-400 my-0.5"></div>
                  <div className="text-xs font-bold text-slate-800">
                    {form.chairmanName}
                  </div>
                </div>

                {/* Vice-Chair */}
                <div className="flex flex-col items-center text-center justify-end h-20">
                  <div className="w-7 h-7 rounded-full bg-[#0A2451] text-amber-400 flex items-center justify-center text-xs mb-1">
                    👤
                  </div>
                  <div className="text-[10px] font-black text-[#0A2451] tracking-wider uppercase">
                    {form.viceChairTitle}
                  </div>
                  <div className="h-[1px] w-12 bg-amber-400 my-0.5"></div>
                  <div className="text-xs font-bold text-slate-800">
                    {form.viceChairName}
                  </div>
                </div>

                {/* Secretary */}
                <div className="flex flex-col items-center text-center justify-end h-20">
                  {certSettings.sig_secretary ? (
                    <img
                      src={`https://rmbbackend.rmbjaipur.com/${certSettings.sig_secretary}`}
                      alt="Secretary Signature"
                      className="h-9 w-auto object-contain mb-0.5"
                    />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-[#0A2451] text-amber-400 flex items-center justify-center text-xs mb-1">
                      ✍️
                    </div>
                  )}
                  <div className="text-[10px] font-black text-[#0A2451] tracking-wider uppercase">
                    {form.secretaryTitle}
                  </div>
                  <div className="h-[1px] w-12 bg-amber-400 my-0.5"></div>
                  <div className="text-xs font-bold text-slate-800">
                    {form.secretaryName}
                  </div>
                </div>

                {/* Treasurer */}
                <div className="flex flex-col items-center text-center justify-end h-20">
                  {certSettings.sig_treasurer ? (
                    <img
                      src={`https://rmbbackend.rmbjaipur.com/${certSettings.sig_treasurer}`}
                      alt="Treasurer Signature"
                      className="h-9 w-auto object-contain mb-0.5"
                    />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-[#0A2451] text-amber-400 flex items-center justify-center text-xs mb-1">
                      💼
                    </div>
                  )}
                  <div className="text-[10px] font-black text-[#0A2451] tracking-wider uppercase">
                    {form.treasurerTitle}
                  </div>
                  <div className="h-[1px] w-12 bg-amber-400 my-0.5"></div>
                  <div className="text-xs font-bold text-slate-800">
                    {form.treasurerName}
                  </div>
                </div>
              </div>
            </DraggableElement>

            {/* GOLDEN AWARD RIBBON SEAL ON BOTTOM LEFT */}
            <DraggableElement
              enabled={isDragModeEnabled}
              position={positions.seal}
              onMove={(pos) => setPositions(p => ({ ...p, seal: pos }))}
              label="Ribbon Seal"
              className="absolute bottom-10 left-12 z-30"
            >
              <div className="flex flex-col items-center">
                <div className="relative w-20 h-20 bg-gradient-to-tr from-amber-600 via-yellow-400 to-amber-300 rounded-full p-1 shadow-xl flex items-center justify-center border-2 border-amber-200">
                  <div className="w-full h-full bg-[#0A2451] rounded-full flex flex-col items-center justify-center text-center border border-amber-400 p-1">
                    <span className="text-amber-400 text-xs">🏆</span>
                    <span className="text-amber-300 font-black text-xs tracking-widest">R2R</span>
                  </div>
                </div>
                <div className="flex gap-1 -mt-2">
                  <div className="w-3.5 h-10 bg-[#0A2451] border-l-2 border-r-2 border-amber-400 transform -rotate-12 rounded-b-sm"></div>
                  <div className="w-3.5 h-10 bg-[#0A2451] border-l-2 border-r-2 border-amber-400 transform rotate-12 rounded-b-sm"></div>
                </div>
              </div>
            </DraggableElement>

            {/* FOOTER SLOGAN & DATE */}
            <DraggableElement
              enabled={isDragModeEnabled}
              position={positions.footer}
              onMove={(pos) => setPositions(p => ({ ...p, footer: pos }))}
              label="Footer Bar"
            >
              <div className="flex justify-between items-center text-[10px] text-slate-600 font-bold tracking-widest uppercase border-t border-slate-300/60 pt-2 mt-2">
                <div className="flex items-center gap-1.5">
                  <FaCalendarAlt className="text-amber-600" />
                  <span>DATE: {form.certificateDate}</span>
                </div>

                <div className="text-[#0A2451] font-black tracking-[0.2em]">
                  FELLOWSHIP <span className="text-amber-500">|</span> BEYOND BUSINESS <span className="text-amber-500">|</span> RELATIONSHIPS FOR LIFE
                </div>
              </div>
            </DraggableElement>

          </div>
        </div>
      </div>

    </div>
  );
};

export default CertificateGenerator;
